import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { get, post, setLogoutCallback, setIsLoggedIn } from '@utils/axios_api';

export const AuthContext = createContext();

const ONE_HOUR = 3_600_000;
const PING_URL = '/auth/me';

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    role: null,
    userId: null,
    name: null,
    staffId: null, // 🌟 1. staffId 초기 상태 추가
  });

  const [isLoading, setLoading] = useState(true);
  const [profileCheckPassed, setProfileCheckPassed] = useState(false);

  const normalizeMe = (me) => {
    if (!me || typeof me !== 'object') return null;

    const isAuth =
      me.isAuthenticated ??
      me.authenticated ??
      me.isAuth ??
      me.loggedIn ??
      false;

    return {
      isAuthenticated: !!isAuth,
      role: me.role ?? me.authority ?? me.authRole ?? null,
      userId: me.userId ?? me.username ?? me.user ?? me.id ?? null,
      name: me.name ?? me.displayName ?? me.nickName ?? null,
      staffId: me.staffId ?? null, // 🌟 2. 응답 데이터에서 staffId 매핑
      ...me,
    };
  };

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pingTimerRef = useRef(null);

  const cacheAuth = (info) => {
    setAuth(info);
  };

  const stopPing = () => clearInterval(pingTimerRef.current);

  const logout = useCallback(
    async (expired = false) => {
      stopPing();
      queryClient.clear();

      try {
        await post('/auth/logout');
      } catch (e) {
        console.error(e);
      }

      setIsLoggedIn(false);
      // 🌟 3. 로그아웃 시 staffId도 null로 초기화
      setAuth({ isAuthenticated: false, role: null, userId: null, name: null, staffId: null });
      setProfileCheckPassed(false);

      navigate('/login', { replace: true });
      toast.info(expired ? '세션이 만료되었습니다. 다시 로그인해 주세요.' : '로그아웃 되었습니다.');
    },
    [navigate, queryClient]
  );

  const startPingSafe = useCallback(() => {
    clearInterval(pingTimerRef.current);
    pingTimerRef.current = setInterval(
      () => get(PING_URL).catch(() => logout(true)),
      ONE_HOUR
    );
  }, [logout]);

  const login = async (form) => {
    await post('/auth/login', form);
    const raw = await get(PING_URL); 
    const me = normalizeMe(raw);

    if (!me?.isAuthenticated) {
      throw new Error('로그인 상태를 확인할 수 없습니다.');
    }

    cacheAuth({ isAuthenticated: true, ...me });
    setIsLoggedIn(true);
    startPingSafe();
  };

  useEffect(() => setLogoutCallback(logout), [logout]);

  useEffect(() => {
    get(PING_URL)
      .then((raw) => {
        const me = normalizeMe(raw);
        if (!me?.isAuthenticated) return;

        cacheAuth(me);
        setIsLoggedIn(true);
        startPingSafe();
      })
      .catch(() => setAuth((a) => ({ ...a, isAuthenticated: false })))
      .finally(() => setLoading(false));

    return stopPing;
  }, [startPingSafe]);

  const passProfileCheck = () => setProfileCheckPassed(true);

  return (
    <AuthContext.Provider
      value={{ auth, login, logout, isLoading, profileCheckPassed, passProfileCheck }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;